import { jLocale } from '@/lib/jurisdiction'

/**
 * Shared date formatting utilities, localised by deployment realm
 * (au → en-AU, us → en-US; see lib/jurisdiction.ts).
 *
 * Single source of truth — do not define formatDate/formatDateTime
 * functions in individual components. Import from here.
 */

/** Parse an ISO string into a Date without a UTC day-shift for date-only
 *  values. A bare "YYYY-MM-DD" is parsed as UTC midnight by `new Date`, which
 *  renders a day early for any viewer west of UTC (US realm, or an AU
 *  clinician travelling) — F-24. Full timestamps (with a "T") are unaffected.
 *  Mirrors the guard `formatDateLabel` already used. */
function parseLocalDate(iso: string): Date {
  return iso.includes('T') ? new Date(iso) : new Date(iso + 'T00:00:00')
}

/** "Mon 6 Apr 2026" — full date with weekday */
export function formatDate(iso: string | null | undefined): string {
  if (!iso) return ''
  return parseLocalDate(iso).toLocaleDateString(jLocale(), {
    weekday: 'short', day: 'numeric', month: 'short', year: 'numeric',
  })
}

/** "6 Apr 2026" — date without weekday */
export function formatDateCompact(iso: string | null | undefined): string {
  if (!iso) return ''
  return parseLocalDate(iso).toLocaleDateString(jLocale(), {
    day: 'numeric', month: 'short', year: 'numeric',
  })
}

/** Whole years between a date-only ISO birth date and today. */
export function ageFromDob(dob: string | null | undefined, today: Date = new Date()): number | null {
  if (!dob) return null
  const b = parseLocalDate(dob)
  if (Number.isNaN(b.getTime())) return null
  let age = today.getFullYear() - b.getFullYear()
  const beforeBirthday =
    today.getMonth() < b.getMonth() ||
    (today.getMonth() === b.getMonth() && today.getDate() < b.getDate())
  if (beforeBirthday) age -= 1
  return age
}

/** "9 Mar 1993 (33)" — the one way a date of birth is shown (CLA-1257).
 *  Front desk reads the age far more often than the date, so it travels
 *  with it. Empty string for no DOB; the raw value if it does not parse,
 *  so a bad record is visible rather than blank. */
export function formatDob(dob: string | null | undefined, today: Date = new Date()): string {
  if (!dob) return ''
  const age = ageFromDob(dob, today)
  if (age == null) return dob
  return `${formatDateCompact(dob)} (${age})`
}

/** "6 Apr" — short date (no year) */
export function formatDateShort(iso: string | null | undefined): string {
  if (!iso) return ''
  return parseLocalDate(iso).toLocaleDateString(jLocale(), {
    day: 'numeric', month: 'short',
  })
}

/** "6 Apr 2:30 pm" — date with time */
export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return ''
  try {
    const d = new Date(iso)
    return d.toLocaleDateString(jLocale(), { day: 'numeric', month: 'short' }) +
      ' ' + d.toLocaleTimeString(jLocale(), { hour: '2-digit', minute: '2-digit' })
  } catch {
    return iso
  }
}

/** "6 Apr 2026, 2:30 pm" (AU) / "Apr 6, 2026, 2:30 PM" (US) — a full
 *  timestamp, for history and audit lines. The month is named, so neither
 *  realm can misread 06/04 as 4 June or 6 April. */
export function formatDateTimeFull(iso: string | null | undefined): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleString(jLocale(), {
    day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit',
  })
}

/** Whether the realm writes the day before the month: AU "6 Apr" (true),
 *  US "Apr 6" (false). For the few labels that spell their own month
 *  abbreviations instead of using the locale's (en-AU's are "June", "Sept"). */
export function dayBeforeMonth(): boolean {
  const order = new Intl.DateTimeFormat(jLocale(), { day: 'numeric', month: 'short' })
    .formatToParts(new Date(2026, 0, 15))
    .map((p) => p.type)
  return order.indexOf('day') < order.indexOf('month')
}

/** "6 Apr" or "6 Apr 2025" (includes year only if different from current year) */
export function formatDateLabel(iso: string | null | undefined): string {
  if (!iso) return ''
  // Handle date-only strings (YYYY-MM-DD) without timezone shift
  const d = parseLocalDate(iso)
  const thisYear = new Date().getFullYear()
  const opts: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short' }
  if (d.getFullYear() !== thisYear) opts.year = 'numeric'
  return d.toLocaleDateString(jLocale(), opts)
}

/** "2:30 pm" (AU) / "2:30 PM" (US) — time only */
export function formatTime(iso: string | null | undefined): string {
  if (!iso) return ''
  return new Date(iso).toLocaleTimeString(jLocale(), { hour: 'numeric', minute: '2-digit' })
}

/** "Today", "Tomorrow", "Yesterday", "In 3 days", "5 days ago", or formatted date */
export function formatRelative(iso: string | null | undefined): string {
  if (!iso) return ''
  const d = new Date(iso)
  const now = new Date()
  const diffDays = Math.ceil((d.getTime() - now.getTime()) / 86400000)
  if (diffDays === 0) return 'Today'
  if (diffDays === 1) return 'Tomorrow'
  if (diffDays === -1) return 'Yesterday'
  if (diffDays > 0 && diffDays <= 7) return `In ${diffDays} days`
  if (diffDays < 0 && diffDays >= -7) return `${Math.abs(diffDays)} days ago`
  return formatDateCompact(iso)
}

/** "2026-04-06" — ISO date string for API params (local date, not UTC) */
export function formatDateParam(date: Date): string {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

/** Local "YYYY-MM-DD" day key for any ISO timestamp (never a UTC slice). */
export function localDayKey(iso: string): string {
  return formatDateParam(new Date(iso))
}

/** Today's local day key. */
export function todayDayKey(): string {
  return formatDateParam(new Date())
}

/** The canonical local encounter-day key for a note: prefers `recorded_at`
 *  (when the consult happened) over `created_at` (when it was uploaded), so a
 *  note recorded offline yesterday and uploaded today files under yesterday —
 *  the same day the timeline groups it under (its `occurred_at`). */
export function noteDayKey(note: { recorded_at?: string | null; created_at: string }): string {
  return localDayKey(note.recorded_at || note.created_at)
}
